import { formatMoney, type OfferCard, STORES, sellerName } from "@larder/shared";
import { StoreMark } from "./StoreMark";

const STORE_COUNT = STORES.length;

/**
 * The offers, to be chosen between.
 *
 * The screen used to pick one listing and put its name and picture at the top,
 * which quietly turned "the packet we happened to price" into "your item". It
 * was wrong more often than it looked: "milk" became one particular carton, and
 * even "hajmola" became the candy pouch when the tablets were just as likely.
 *
 * Nothing can know which one is meant, so this stops guessing and shows them —
 * the pack, the shop, the price and the score — and lets the household point at
 * one. Which is also more useful than the guess ever was.
 *
 * A horizontal row rather than a list: these are alternatives to compare, and
 * things you compare belong side by side. It also keeps a long answer to one
 * screen-height, which matters on a page that already has shops and macros
 * below it.
 */
export function OfferCards({
  cards,
  status = "ready",
  source = "none",
}: {
  cards: readonly OfferCard[];
  status?: "idle" | "loading" | "ready";
  source?: "cache" | "live" | "stale" | "none";
}) {
  // No city yet: the doorway in front of the screen is asking for one, and a
  // skeleton under a question would be two things happening at once.
  if (status === "idle") return null;

  /**
   * Loading is a shape, not a word. Three card-sized blanks say "something
   * this size is coming" in a way a spinner never does, and the caption names
   * the feature, so a slow or empty answer reads as "it looked" rather than
   * "there is nothing here".
   */
  if (status === "loading") {
    return (
      <div className="offers-wait" data-testid="offers-loading" aria-live="polite">
        <p className="offers-caption">
          <span className="offers-dot" aria-hidden="true" />
          Checking prices at {STORE_COUNT} shops…
        </p>
        <div className="offers" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="offer offer-skeleton">
              <div className="offer-shot skeleton" />
              <span className="skeleton line price" />
              <span className="skeleton line" />
              <span className="skeleton line short" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  /**
   * An empty answer says why. The two reasons look identical as a blank row
   * and mean opposite things: `none` is "we could not ask" (the month's
   * allowance is spent, or the lookup failed) and comes back tomorrow;
   * `cache`/`live` with no offers is "we asked, and nobody lists this".
   */
  if (cards.length === 0) {
    return (
      <p className="offers-caption quiet" data-testid="offers-empty" data-reason={source}>
        {source === "none"
          ? "Prices aren't available right now — the shops below still work."
          : "No shop lists this online right now."}
      </p>
    );
  }

  return (
    <div className="offers" data-testid="offers">
      {cards.map((c) => (
        <a
          key={`${c.seller}|${c.title}`}
          className="offer"
          href={c.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`offer-${c.seller}`}
        >
          <div className="offer-shot">
            {c.thumbnail && (
              <img
                src={c.thumbnail}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                /* The one hot-linked image in the app — a product picture
                   cannot be committed the way a shop's logo can. No referrer,
                   so Google's CDN is not told which screen wanted it. */
                referrerPolicy="no-referrer"
              />
            )}
            {/* The genuinely lowest price in the row, wherever it landed —
                the row is grouped by shop before it is priced, so the cheapest
                is often not the first. A badge meaning "cheapest of the ones we
                like" would be a lie told by omission. */}
            {c.best && (
              <span className="offer-best" data-testid="cheapest">
                Cheapest
              </span>
            )}
          </div>

          <span className="offer-price">
            {formatMoney(c.priceMinor)}
            {/* Only ever shown when the listing really is a reduction. */}
            {c.oldPriceMinor && (
              <span className="offer-was">{formatMoney(c.oldPriceMinor)}</span>
            )}
          </span>

          <span className="offer-title">{c.title}</span>

          <span className="offer-seller">
            {/* The mark for a shop we can open properly; the name alone for
                everyone else, which is also the honest signal that tapping it
                leads to a web page rather than an app. */}
            {c.store && <StoreMark id={c.store} />}
            <span className="offer-seller-name">{sellerName(c)}</span>
          </span>

          {c.rating !== null && (
            <span className="offer-rating">
              ★ {c.rating.toFixed(1)}
              {c.reviews ? ` (${compact(c.reviews)})` : ""}
            </span>
          )}
        </a>
      ))}
    </div>
  );
}

/**
 * "152.2k" rather than "152,214".
 *
 * Nobody reads a review count as a number; they read it as a size. Three
 * characters says the same thing as seven and fits a card this wide.
 */
function compact(n: number): string {
  if (n >= 100_000) return `${Math.round(n / 100_000)}L`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}k`;
  return String(n);
}
